import { Alert, Text } from '@mantine/core';
import { lazy } from 'react';
import LazyModalBoundary from '@/components/LazyModalBoundary.tsx';
import { useSearchParams } from 'react-router-dom';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import useRetrieveAutomationVersion from '@/hooks/api/automationVersions/useRetrieveAutomationVersion.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';

const VersionDocumentationModal = lazy(() => import('./VersionDocumentationModal.tsx'));

export default function VersionBlueprintInspector() {
  const [searchParams, setSearchParams] = useSearchParams();
  const versionId = searchParams.get('blueprintVersion') ?? undefined;
  const versionQuery = useRetrieveAutomationVersion(versionId);
  const close = () => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('blueprintVersion');
        return next;
      },
      { replace: true },
    );
  };
  const versionMismatch = !!versionQuery.data && versionQuery.data.id !== versionId;

  if (!versionId) return null;

  if (versionQuery.data && !versionMismatch) {
    return (
      <LazyModalBoundary
        resetKey={versionId}
        onClose={close}
        title="Version blueprint"
        closeLabel="Close Blueprint inspector"
      >
        <VersionDocumentationModal version={versionQuery.data} onClose={close} />
      </LazyModalBoundary>
    );
  }

  return (
    <StyledModal
      opened
      onClose={close}
      title="Version blueprint"
      withCloseButton
      closeButtonProps={{ 'aria-label': 'Close Blueprint inspector' }}
    >
      {versionQuery.isPending ? <Text size="sm">Loading blueprint…</Text> : null}
      {versionQuery.isError || versionMismatch ? (
        <Alert color="red" title="Blueprint unavailable">
          {versionMismatch
            ? 'Blueprint response does not belong to the requested version.'
            : getHttpErrorMessage(versionQuery.error)}
          <StyledButton
            variant="subtle"
            disabled={versionQuery.isFetching}
            onClick={() => void versionQuery.refetch()}
          >
            Retry Blueprint
          </StyledButton>
        </Alert>
      ) : null}
    </StyledModal>
  );
}
